import React from 'react';
import { ReceiveChatConversationMessagesDto } from '@endion/types';
import ReactDOM from 'react-dom/client';
import App, { AppProps } from './components/app';
import { ENDION_CHATBOT_UPDATE_CONTEXT_EVENT_NAME, ENDION_CHATBOT_UPDATE_STATE_EVENT_NAME } from './utils/events';

export const MOUNT_ELEMENT_DATA_ATTR = 'data-endion-chatbot-mount-element';
export const REACT_CONTAINER_ID = 'endion-chatbot-root';

export type EndionDebug = {
  simulatedOrigin?: string;
};

export type CreateEndionChatbotOptions = {
  mountElement?: HTMLElement | null;
  automaticRemount: boolean;
  chatbotApiKey: string;
  endion_debug?: EndionDebug;
  // TODO: Remove this option once Meta review is completed. This should be managed through the bot config instead
  replaceDefaultClickBehaviorWithLink?: {
    // This option is used for whatsapp demonstration purposes
    href: string;
  };
  context?: ReceiveChatConversationMessagesDto['context'];
  showTrigger?: boolean;
};

let mutationObserver: MutationObserver | null = null;

//! TODO: Update this method to return the actual instance of the chatbot, this way we could allow external manipulation without using events.
//! TODO: Add context validation based on zod
export const createEndionChatbot = ({
  mountElement,
  chatbotApiKey,
  automaticRemount,
  endion_debug,
  replaceDefaultClickBehaviorWithLink,
  context,
  showTrigger = true,
}: CreateEndionChatbotOptions) => {
  if (!chatbotApiKey) {
    throw new Error('Endion ChatBot: API KEY IS MISSING');
  }

  let rootElements: { newRoot: HTMLElement; reactContainer: HTMLElement };

  // Remove the observer if already exists - this only happens if this create function is called more than one single time.
  if (mutationObserver) {
    mutationObserver.disconnect();
  }

  window.endion_debug = endion_debug;

  if (mountElement) {
    const shadowRoot = attachShadowRootToMountElement(mountElement);
    rootElements = { newRoot: mountElement, reactContainer: createReactContainer(shadowRoot) };
  } else {
    rootElements = createNewRootElement();
  }

  let parentElement = rootElements.newRoot.parentElement as HTMLElement;

  //! This process remounts the chatbot in case there's a full page re-render and the mounting node is removed.
  // Useful for certain SPAs
  if (automaticRemount && parentElement) {
    mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach(() => {
        if (!parentElement.contains(rootElements.newRoot)) {
          const isParentElementOnPage =
            parentElement && parentElement.parentNode && document.contains(parentElement.parentNode);

          parentElement = (isParentElementOnPage ? parentElement : document.body) as HTMLElement;
          rootElements = createNewRootElement(parentElement);

          return mountApp(rootElements, {
            chatbotApiKey,
            isRemounted: true,
            replaceDefaultClickBehaviorWithLink,
            context,
            showTrigger,
          });
        }
      });
    });

    mutationObserver.observe(parentElement, { attributes: false, childList: true, subtree: false });
  }

  return mountApp(rootElements, { chatbotApiKey, replaceDefaultClickBehaviorWithLink, context, showTrigger });
};

const mountApp = (mountElement: { newRoot: HTMLElement; reactContainer: HTMLElement }, appProps: AppProps) => {
  return ReactDOM.createRoot(mountElement.reactContainer).render(
    <React.StrictMode>
      <App {...appProps} />
    </React.StrictMode>,
  );
};

const attachShadowRootToMountElement = (mountElement: HTMLElement) => {
  if (!mountElement.shadowRoot) {
    mountElement.attachShadow({ mode: 'open' });
  }
  if (!mountElement.shadowRoot) {
    throw new Error('Endion ChatBot: Shadow DOM is not supported in this browser');
  }

  //! Used to identify the mount element within the app code.
  mountElement.dataset.endionChatbotMountElement = 'true';

  injectStyles(mountElement.shadowRoot);

  return mountElement.shadowRoot;
};

const createReactContainer = (shadowRoot: ShadowRoot) => {
  // Create a container for React inside the Shadow DOM
  const reactContainer = document.createElement('div');
  reactContainer.id = REACT_CONTAINER_ID;
  shadowRoot.appendChild(reactContainer);

  return reactContainer;
};

const createNewRootElement = (parentElement: HTMLElement = document.body) => {
  const newRoot = document.createElement('div');
  const shadowRoot = attachShadowRootToMountElement(newRoot);
  const reactContainer = createReactContainer(shadowRoot);

  parentElement.appendChild(newRoot);

  return { newRoot, reactContainer };
};

// TODO: we should also set the line-height https://gourav.io/blog/tailwind-in-shadow-dom
const injectStyles = (shadowRoot: ShadowRoot) => {
  const linkElem = document.createElement('link');
  linkElem.setAttribute('rel', 'stylesheet');
  linkElem.setAttribute(
    'href',
    import.meta.env.DEV
      ? './src/chatbot-styles-dev.css'
      : import.meta.env.VITE_CSS_LOCAL || import.meta.env.VITE_CSS_PRODUCTION_URL,
  );
  shadowRoot.appendChild(linkElem);
};

/**
 * Forwards the new context through a event that's listened within the react application
 * @param newContext
 */
export const updateChatbotContext = (newContext: any) => {
  document.dispatchEvent(
    new CustomEvent(ENDION_CHATBOT_UPDATE_CONTEXT_EVENT_NAME, {
      bubbles: true,
      cancelable: true,
      detail: newContext,
    }),
  );
};

export type UpdateChatbotStateOptions = {
  isOpen?: boolean;
  showTrigger?: boolean;
};

/**
 * Updates the chatbot's internal state from external code by dispatching a custom event.
 * This function serves as the primary interface for external applications to control
 * various aspects of the chatbot's state and behavior.
 *
 * @param options - Configuration object for state updates
 * @param options.isOpen - boolean to set the chatbot open (true) or closed (false)
 * @param options.showTrigger - boolean to show (true) or hide (false) the trigger button
 */
export const updateChatbotState = ({ isOpen, showTrigger }: UpdateChatbotStateOptions = {}) => {
  document.dispatchEvent(
    new CustomEvent(ENDION_CHATBOT_UPDATE_STATE_EVENT_NAME, {
      bubbles: true,
      cancelable: true,
      detail: { isOpen, showTrigger },
    }),
  );
};
